import { splitCell } from "../utils/panelGeometry";
import { markPlaced } from "../utils/motion";

/**
 * The first page a brand-new project opens on: a small, finished-looking
 * comic page whose lettering teaches the gestures it was made with. Text,
 * fonts, and colours come from the active comic style, so it looks right in
 * every preset.
 */

const bubble = (id, text, x, y, width, height, extra = {}) => ({
  id: markPlaced(id),
  type: "speechBubble",
  text,
  shape: "rounded",
  tailEnabled: true,
  x,
  y,
  width,
  height,
  tails: [{ id: `${id}-tail`, tipX: width * 0.3, tipY: height + 26, width: 18 }],
  ...extra,
});

const sfx = (id, text, x, y, extra = {}) => ({
  id: markPlaced(id),
  type: "sfx",
  text,
  fontSize: 56,
  rotation: -10,
  x,
  y,
  ...extra,
});

/**
 * @param {number} pageWidth  page px
 * @param {number} pageHeight page px
 * @param {number} gutter     page px
 */
export const buildSamplePage = (pageWidth, pageHeight, gutter) => {
  const g = gutter;
  const innerW = pageWidth - g * 2;
  const innerH = pageHeight - g * 2;
  const rowH = [0.36, 0.3, 0.34].map((f) => (innerH - g * 2) * f);
  const y1 = g;
  const y2 = y1 + rowH[0] + g;
  const y3 = y2 + rowH[1] + g;

  // Row 1: one wide panel, cut on a diagonal with the knife
  const wide = { id: "sample-r1", key: "R1", x: g, y: y1, width: innerW, height: rowH[0], items: [] };
  const [left, right] = splitCell(
    wide,
    { x: g + innerW * 0.5, y: y1 + rowH[0] },
    { x: g + innerW * 0.64, y: y1 },
    g
  ).sort((a, b) => a.x - b.x);

  left.key = "R1a";
  left.items = [
    bubble("sample-hello", "Welcome to the drafting table! This page is a sample — poke at everything.", 18, 18, 210, 96),
  ];
  right.key = "R1b";
  const rightOffset = right.x - g;
  right.items = [
    sfx("sample-snip", "SNIP!", innerW * 0.66 - rightOffset, rowH[0] * 0.55),
    bubble("sample-knife", "I was cut with the knife: hold ⌥ and drag across a panel.", innerW - rightOffset - 230, 18, 210, 86, {
      tails: [{ id: "sample-knife-tail", tipX: 30, tipY: 120, width: 18 }],
    }),
  ];

  // Row 2: a narrow and a wide panel
  const narrowW = (innerW - g) * 0.38;
  const r2a = { id: "sample-r2a", key: "R2a", x: g, y: y2, width: narrowW, height: rowH[1], items: [
    bubble("sample-gutter", "Drag a gutter to resize both sides.", 14, 16, Math.min(200, narrowW - 28), 76),
  ] };
  const r2b = { id: "sample-r2b", key: "R2b", x: g + narrowW + g, y: y2, width: innerW - narrowW - g, height: rowH[1], items: [
    bubble("sample-letter", "Double-click empty paper to letter there.", 24, 20, 220, 70),
    sfx("sample-tap", "TAP TAP", 290, rowH[1] * 0.45, { fontSize: 44, rotation: 6 }),
  ] };

  // Row 3: three panels
  const thirdW = (innerW - g * 2) / 3;
  const r3 = [0, 1, 2].map((i) => ({
    id: `sample-r3${i}`, key: `R3${"abc"[i]}`, x: g + i * (thirdW + g), y: y3, width: thirdW, height: rowH[2], items: [],
  }));
  r3[0].items = [bubble("sample-art", "Drop or paste art onto any panel.", 14, 18, Math.min(190, thirdW - 28), 80)];
  r3[1].items = [bubble("sample-zoom", "⌘-scroll to zoom. Hold Space and drag to pan.", 14, 18, Math.min(190, thirdW - 28), 90, {
    shape: "cloud", tails: [{ id: "sample-zoom-tail", tipX: 60, tipY: 140, width: 18 }],
  })];
  r3[2].items = [bubble("sample-export", "Export the whole comic from the page strip.", 14, 18, Math.min(190, thirdW - 28), 80, {
    shape: "narration", tailEnabled: false,
  })];

  return [left, right, r2a, r2b, ...r3];
};
